import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Chip, IconButton } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useRouter } from "next/router";
import { assetUrl } from "@/utils/asset";
import { common, purple } from "@/theme/colors";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const AUTO_ADVANCE_MS = 6000;
const FADE_MS = 800;
const Wrapper = styled(Box)({
  position: "relative",
  width: "100%",
  height: 480,
  overflow: "hidden",
  "@media (max-width: 600px)": {
    height: 380
  }
});
const Slide = styled(Box, {
  shouldForwardProp: prop => prop !== "$visible"
})(({
  $visible
}) => ({
  position: "absolute",
  inset: 0,
  transition: `opacity ${FADE_MS}ms ease-in-out`,
  opacity: $visible ? 1 : 0,
  pointerEvents: $visible ? "auto" : "none"
}));
const BgImage = styled("div", {
  shouldForwardProp: prop => prop !== "$src"
})(({
  $src
}) => ({
  position: "absolute",
  inset: 0,
  backgroundImage: `url('${$src}')`,
  backgroundSize: "cover",
  backgroundPosition: "center"
}));
const Overlay = styled(Box)({
  position: "absolute",
  inset: 0,
  background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.2) 100%)"
});
const Content = styled(Box)(({
  theme
}) => ({
  position: "relative",
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  height: "100%",
  padding: "25px",
  [theme.breakpoints.up("sm")]: {
    padding: "50px"
  },
  [theme.breakpoints.up("md")]: {
    padding: "100px"
  },
  [theme.breakpoints.up("lg")]: {
    padding: "120px"
  },
  [theme.breakpoints.up("xl")]: {
    padding: "120px"
  }
}));
const TagRow = styled(Box)(({
  theme
}) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5)
}));
const CtaButton = styled("button")(({
  theme
}) => ({
  background: purple.primary,
  color: common.white,
  border: "none",
  borderRadius: 6,
  padding: "10px 28px",
  fontSize: "0.95rem",
  fontWeight: 600,
  cursor: "pointer",
  marginTop: theme.spacing(2.5),
  alignSelf: "flex-start",
  transition: "background 0.2s",
  "&:hover": {
    background: purple.dark
  }
}));
const DotRow = styled(Box)({
  position: "absolute",
  bottom: 20,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: 10,
  zIndex: 2
});
const Dot = styled("button", {
  shouldForwardProp: prop => prop !== "$active"
})(({
  $active
}) => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  border: `2px solid ${common.white}`,
  background: $active ? common.white : "transparent",
  cursor: "pointer",
  padding: 0,
  transition: "background 0.3s"
}));
const ArrowBtn = styled(IconButton)(({
  theme
}) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 2,
  color: alpha(common.white, 0.8),
  backgroundColor: alpha("#000", 0.3),
  "&:hover": {
    backgroundColor: alpha("#000", 0.5)
  },
  [theme.breakpoints.down("sm")]: {
    display: "none"
  }
}));
export default function NewsCarousel({
  items
}) {
  const [active, setActive] = useState(0);
  const paused = useRef(false);
  const router = useRouter();
  const count = items.length;
  const next = useCallback(() => {
    setActive(i => (i + 1) % count);
  }, [count]);
  const prev = useCallback(() => {
    setActive(i => (i - 1 + count) % count);
  }, [count]);

  // Auto-advance
  useEffect(() => {
    if (count <= 1) return;
    const id = setInterval(() => {
      if (!paused.current) next();
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [count, next]);

  // Touch swipe
  const touchX = useRef(null);
  const handleTouchStart = e => {
    touchX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = e => {
    if (touchX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(diff) > 50) {
      diff < 0 ? next() : prev();
    }
    touchX.current = null;
  };
  if (count === 0) return null;
  return /*#__PURE__*/_jsxs(Wrapper, {
    onMouseEnter: () => paused.current = true,
    onMouseLeave: () => paused.current = false,
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
    "aria-label": "News Carousel",
    children: [items.map((item, idx) => /*#__PURE__*/_jsxs(Slide, {
      $visible: idx === active,
      children: [/*#__PURE__*/_jsx(BgImage, {
        $src: assetUrl(item.backgroundImage)
      }), /*#__PURE__*/_jsx(Overlay, {}), /*#__PURE__*/_jsxs(Content, {
        children: [/*#__PURE__*/_jsxs(Box, {
          sx: {
            display: "flex",
            flexDirection: "column",
            gap: 0
          },
          children: [/*#__PURE__*/_jsxs(TagRow, {
            children: [/*#__PURE__*/_jsx(Chip, {
              label: item.tag,
              size: "small",
              sx: {
                bgcolor: item.ctaColor || purple.primary,
                color: common.white,
                fontWeight: 600,
                fontSize: "0.75rem"
              }
            }), /*#__PURE__*/_jsx(Typography, {
              variant: "caption",
              sx: {
                color: alpha(common.white, 0.8)
              },
              children: item.date
            })]
          }), /*#__PURE__*/_jsx(Typography, {
            variant: "h3",
            sx: {
              color: common.white,
              fontWeight: 700,
              lineHeight: 1.2,
              fontSize: {
                xs: "1.5rem",
                sm: "2rem",
                md: "2.5rem"
              }
            },
            children: item.title
          }), /*#__PURE__*/_jsx(Typography, {
            variant: "body1",
            sx: {
              color: alpha(common.white, 0.85),
              mt: 1,
              maxWidth: 600,
              fontSize: {
                xs: "0.9rem",
                md: "1.05rem"
              }
            },
            children: item.subtitle
          })]
        }), /*#__PURE__*/_jsx(Box, {
          children: /*#__PURE__*/_jsx(CtaButton, {
            style: item.ctaColor ? {
              background: item.ctaColor
            } : undefined,
            onClick: () => router.push(item.ctaUrl),
            children: "Read more"
          })
        })]
      })]
    }, idx)), count > 1 && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ArrowBtn, {
        sx: {
          left: 16
        },
        onClick: prev,
        children: /*#__PURE__*/_jsx(ArrowBackIosNewIcon, {
          fontSize: "small"
        })
      }), /*#__PURE__*/_jsx(ArrowBtn, {
        sx: {
          right: 16
        },
        onClick: next,
        children: /*#__PURE__*/_jsx(ArrowForwardIosIcon, {
          fontSize: "small"
        })
      })]
    }), count > 1 && /*#__PURE__*/_jsx(DotRow, {
      children: items.map((_, idx) => /*#__PURE__*/_jsx(Dot, {
        $active: idx === active,
        onClick: () => setActive(idx),
        "aria-label": `Go to slide ${idx + 1}`
      }, idx))
    })]
  });
}